import { WikiLayout } from "@/components/WikiLayout";
import { WikiPage } from "@/components/WikiPage";
import { AlertTriangle, Lightbulb, CheckCircle2, Settings } from "lucide-react";

const Actuators = () => {
  return (
    <WikiLayout>
      <WikiPage 
        title="Valves & Actuators" 
        description="Understanding and verifying automated valve systems"
      >
        <section className="space-y-8">
          {/* What Actuators Do */}
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-6">
            <h2 className="text-xl font-semibold text-foreground mb-3 flex items-center gap-2">
              <Settings className="h-5 w-5 text-primary" />
              What Actuators Do
            </h2>
            <p className="text-muted-foreground">
              Actuators are electric motors mounted on valves that allow the automation system to 
              change valve positions remotely. They control which water paths are open — enabling 
              Pool Mode, Spa Mode, spillover features, and other automated functions without 
              manual valve adjustment.
            </p>
          </div>

          {/* Toggle Switches */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Toggle Switches</h2>
            <p className="text-muted-foreground mb-4">
              Most actuators have a toggle switch that controls whether the motor responds to automation 
              commands. This is critical to understand.
            </p>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-primary/10 border border-primary/30 rounded-lg p-4">
                <h4 className="font-semibold text-primary mb-2">ON (Auto)</h4>
                <p className="text-sm text-muted-foreground">
                  Normal operation. Actuator responds to automation commands. This is how it should be.
                </p>
              </div>
              <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4">
                <h4 className="font-semibold text-destructive mb-2">OFF</h4>
                <p className="text-sm text-muted-foreground">
                  Motor disabled. Automation cannot move this valve. Can cause serious problems.
                </p>
              </div>
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                <h4 className="font-semibold text-amber-600 mb-2">Manual Override</h4>
                <p className="text-sm text-muted-foreground">
                  Some models have 3 positions. Middle/manual allows hand adjustment.
                </p>
              </div>
            </div>
          </div>

          {/* Critical Warning */}
          <div className="bg-destructive/10 border-2 border-destructive rounded-lg p-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-6 w-6 text-destructive mt-0.5 shrink-0" />
              <div>
                <h3 className="text-lg font-semibold text-destructive mb-2">Critical: Actuator Toggle Issue</h3>
                <p className="text-muted-foreground mb-4">
                  If an actuator toggle is accidentally switched OFF, the automation system cannot rotate the valve. 
                  This can cause serious problems that aren't immediately visible:
                </p>
                <div className="bg-background rounded-lg p-4 border">
                  <p className="font-medium text-foreground mb-2">Real Example: Spa Drain Event</p>
                  <p className="text-sm text-muted-foreground">
                    When a return-valve actuator toggle was OFF, the system operated normally in Pool Mode. 
                    But when Spa Mode was activated, the valve couldn't rotate — so suction pulled from the spa 
                    while returns went to the pool. <strong>Result: The spa drained rapidly.</strong>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Pre-Service Check */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Pre-Service Equipment Check</h2>
            <p className="text-muted-foreground mb-4">
              Before starting service work, do a quick visual inspection of the equipment pad.
            </p>
            <div className="bg-card border rounded-lg p-6">
              <ul className="space-y-2 text-muted-foreground">
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Note actuator toggle positions (all should be ON)</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Observe current valve positions</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Note any irregularities before touching anything</li>
                <li><CheckCircle2 className="h-4 w-4 text-primary inline mr-2" />Take a baseline photo if something seems off</li>
              </ul>
            </div>
          </div>

          {/* Post-Service Verification */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Post-Service Verification Checklist</h2>
            <p className="text-muted-foreground mb-4">
              Before leaving any property with a pool/spa combo or automated valves, complete this verification.
            </p>
            <div className="bg-card border-2 border-primary rounded-lg p-6">
              <ol className="space-y-4 text-muted-foreground">
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">1</span>
                  <div>
                    <strong className="text-foreground">Confirm all actuator toggles are ON</strong>
                    <p className="text-sm mt-1">Visually check each actuator on the equipment pad</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">2</span>
                  <div>
                    <strong className="text-foreground">Cycle Spa Mode via automation</strong>
                    <p className="text-sm mt-1">Use the control panel or app to switch to Spa Mode</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">3</span>
                  <div>
                    <strong className="text-foreground">Watch for valve rotation</strong>
                    <p className="text-sm mt-1">Listen for actuator motor, watch valve indicator move</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">4</span>
                  <div>
                    <strong className="text-foreground">Visual confirmation of spa-return flow</strong>
                    <p className="text-sm mt-1">Verify water is actually returning to the spa, not the pool</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-primary text-primary-foreground w-6 h-6 rounded-full flex items-center justify-center text-sm shrink-0">5</span>
                  <div>
                    <strong className="text-foreground">Return to Pool Mode</strong>
                    <p className="text-sm mt-1">Switch back and verify valve returns to pool position</p>
                  </div>
                </li>
              </ol>
            </div>
          </div>

          {/* During Service */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">During Service</h2>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-6">
              <h4 className="font-semibold text-amber-600 mb-3 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Avoid Accidental Contact
              </h4>
              <p className="text-muted-foreground mb-4">
                Actuator toggle switches are exposed and can be inadvertently bumped during normal service 
                activities. Be aware of their location and avoid bracing on or near actuators.
              </p>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Know where the actuators are on each equipment pad</li>
                <li>• Don't lean or brace on equipment near actuators</li>
                <li>• If you need to reach over actuators, do so carefully</li>
                <li>• The toggle switches are small and lightly tensioned — easy to bump accidentally</li>
              </ul>
            </div>
          </div>

          {/* Limit Stops */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Limit Stops</h2>
            <p className="text-muted-foreground mb-4">
              Actuators have physical limit stops that define how far the valve can rotate in each direction. 
              Incorrect limits can prevent full valve travel.
            </p>
            <div className="bg-card border rounded-lg p-6">
              <h4 className="font-semibold text-foreground mb-3">When to Check Limits</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Valve doesn't rotate fully to either position</li>
                <li>• Actuator motor sounds like it's straining</li>
                <li>• Mode switch works in one direction but not the other</li>
                <li>• After actuator replacement or repositioning</li>
              </ul>
              <p className="text-sm text-muted-foreground mt-4 italic">
                Limit adjustment requires removing the actuator cap and adjusting internal stops. 
                Notify the office if you suspect limit issues — this may require scheduling a repair visit.
              </p>
            </div>
          </div>

          {/* Troubleshooting */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Troubleshooting</h2>
            <div className="space-y-4">
              <div className="bg-card border rounded-lg p-4">
                <h4 className="font-semibold text-foreground mb-2">Actuator Not Responding to Commands</h4>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>• Check toggle switch — is it ON?</li>
                  <li>• Verify automation system is commanding the correct output</li>
                  <li>• Check wiring connection at actuator</li>
                  <li>• Listen for motor trying to run</li>
                </ul>
              </div>
              <div className="bg-card border rounded-lg p-4">
                <h4 className="font-semibold text-foreground mb-2">Grinding or Straining Sounds</h4>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>• Limit stops may be incorrect</li>
                  <li>• Valve may be stuck or binding</li>
                  <li>• Motor failing — may need replacement</li>
                </ul>
              </div>
              <div className="bg-card border rounded-lg p-4">
                <h4 className="font-semibold text-foreground mb-2">Valve Only Partially Rotating</h4>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>• Limit stop needs adjustment</li>
                  <li>• Debris in valve preventing full travel</li>
                  <li>• Motor gear slipping (replacement needed)</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Optional Protection */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Optional Toggle Protection</h2>
            <div className="bg-card border rounded-lg p-6">
              <p className="text-muted-foreground mb-4">
                Actuators ship without protective guards for the toggle switch. For pools with history of 
                accidental toggling or client concerns, a protective cover can be installed.
              </p>
              <div className="bg-primary/10 rounded-lg p-4">
                <h4 className="font-semibold text-foreground mb-2">Cap'n Cover</h4>
                <p className="text-sm text-muted-foreground">
                  The only commercially available retrofit we've identified. Approximately $50 per actuator. 
                  Covers the toggle while allowing intentional access. Can be installed upon client request.
                </p>
              </div>
            </div>
          </div>

          {/* Baseline Documentation */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Baseline Documentation</h2>
            <div className="bg-card border rounded-lg p-6">
              <p className="text-muted-foreground mb-4">
                For each pool/spa system, we maintain documentation of standard valve and actuator settings. 
                This helps quickly identify if something has been changed.
              </p>
              <ul className="space-y-2 text-muted-foreground">
                <li><Lightbulb className="h-4 w-4 text-primary inline mr-2" />Take reference photos of correct actuator positions on first visit</li>
                <li><Lightbulb className="h-4 w-4 text-primary inline mr-2" />Note which valves control Pool Mode vs Spa Mode</li>
                <li><Lightbulb className="h-4 w-4 text-primary inline mr-2" />Document any unique configurations</li>
                <li><Lightbulb className="h-4 w-4 text-primary inline mr-2" />Use as quick reference on future visits</li>
              </ul>
            </div>
          </div>

          {/* Why It Matters */}
          <div className="bg-card border-2 border-primary rounded-lg p-6">
            <h3 className="text-lg font-semibold text-primary mb-3">Why This Matters</h3>
            <p className="text-muted-foreground">
              A misconfigured actuator can cause serious issues that aren't immediately visible. The spa 
              drain incident shows how a simple toggle can lead to a significant problem that only appears 
              when the customer uses Spa Mode — potentially days after our service. The verification 
              checklist prevents this by catching issues before we leave the property.
            </p>
          </div>
        </section>
      </WikiPage>
    </WikiLayout>
  );
};

export default Actuators;